import type { CSSProperties, ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FeatureConfiguration } from '../../config/FeatureConfiguration';
import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { useIsLargerThanWide } from '../../styles/hooks/media';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { useVideoRef } from '../video_v8/VideoRefContext';

import { AmbientLight } from './ambientLight/AmbientLight';
import { PosterBlur } from './posterBlur/PosterBlur';
import type { StreamProps } from './types';
import { useAutomaticallyPosterCreate } from './useAutomaticallyPosterCreate';

import styles from './stream.module.css';

const DEBOUNCE = 100;
const DEFAULT_ASPECT_RATIO = 0.5625; // 16:9 aspect ratio (divide 9 by 16)

/**
 * # Stream
 *
 * A component that renders a stream with a video and an overlay.
 * */
export const Stream = ({
  bottomAllowedCrop = 0,
  bottomOffset = 0,
  className,
  classic,
  curtainBase,
  curtainOverlay,
  gameContainerRef,
  minAspectRatio = 1,
  onResize,
  onInstantResize,
  onViewableAreaChange,
  overlay,
  overlayFade,
  overlayHeight,
  adaptOverlayHeight = true,
  renderVideo,
  poster,
  setPoster,
  posterBlur,
  ...rest
}: StreamProps): ReactElement | null => {
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const originRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const throttleRef = useRef<(number | undefined)[]>([]);
  const isVideoPlayingRef = useRef<boolean>(false);
  const instantResizeThrottleRef = useRef<number>();

  const [videoHeight, setVideoHeight] = useState<number>(0);
  const [allowedCropHeight, setAllowedCropHeight] = useState<number>(0);
  const [computedCrop, setComputedCrop] = useState<number>(0);
  const [overlayOverlap, setOverlayOverlap] = useState<number>(0);
  const [loaderTopOffset, setLoaderTopOffset] = useState<number | null>(null);
  const { videoRef } = useVideoRef();
  const [posterRef, setPosterRef] = useState<RefObject<HTMLImageElement> | null>(null);
  const [noTransition, setNoTransition] = useState<boolean>(!!classic);

  const isWide = useIsLargerThanWide();
  const isIOS = DeviceInfo.getPlatform().IS_IOS;

  const setVideoPlaying = useCallback((isPlaying: boolean): void => {
    isVideoPlayingRef.current = isPlaying;
  }, []);

  useEffect(() => {
    if (classic) {
      setNoTransition(true);
    } else {
      const timeout = window.setTimeout(() => {
        setNoTransition(false);
      }, 100);

      return () => {
        window.clearTimeout(timeout);
      };
    }
  }, [classic]);

  useEffect(() => {
    if (onResize && videoContainerRef.current && videoHeight) {
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry: ResizeObserverEntry) => {
          if (entry.target === videoContainerRef.current) {
            const { x, y, height, width } = entry.contentRect;
            const newOrigin = { x: x + width / 2, y: y + height / 2 };
            if (newOrigin.x === originRef.current.x && newOrigin.y === originRef.current.y) return;
            if (throttleRef.current[0]) {
              clearTimeout(throttleRef.current[0]);
            }
            throttleRef.current[0] = setTimeout(() => {
              onResize({ height, width, origin: newOrigin });
              originRef.current = newOrigin;
              throttleRef.current[0] = undefined;
            }, DEBOUNCE) as unknown as number;
            const videoWrapper = entry.target.children[0];
            if (videoWrapper) {
              // Terrible place and needs to be refactored
              const { width: videoWidth } = videoWrapper.getBoundingClientRect();
              if (videoWidth < width) {
                entry.target.classList.add(styles.stream__videoContainer_showCurtains);
              } else {
                entry.target.classList.remove(styles.stream__videoContainer_showCurtains);
              }
            }
          }
        });
      });

      const videoContainer = videoContainerRef.current;

      resizeObserver.observe(videoContainer);

      return () => {
        resizeObserver.unobserve(videoContainer);
      };
    }
  }, [onResize, videoContainerRef, videoHeight]);

  useEffect(() => {
    if (onInstantResize && videoContainerRef.current) {
      if (instantResizeThrottleRef.current === videoHeight) return;
      instantResizeThrottleRef.current = videoHeight;
      // Terrible place and needs to be refactored
      const { x, y } = videoContainerRef.current.getBoundingClientRect();
      if (throttleRef.current[1]) {
        clearTimeout(throttleRef.current[1]);
      }
      throttleRef.current[1] = setTimeout(() => {
        const width = videoHeight * DEFAULT_ASPECT_RATIO;
        const newOrigin = { x: x + width / 2, y: y + videoHeight / 2 };

        onInstantResize({ height: videoHeight, width, origin: newOrigin });
        throttleRef.current[1] = undefined;
      }, DEBOUNCE) as unknown as number;
    }
  }, [onInstantResize, videoContainerRef, videoHeight, instantResizeThrottleRef]);

  useEffect(() => {
    const handleResize = (): void => {
      if (!gameContainerRef?.current) return;

      let containerHeight;
      let containerWidth;

      if (isIOS) {
        /* document.documentElement.clientHeight for iOS (to handle Safari's dynamic viewport changes)
          Known issue since iOS 9: https://gist.github.com/Linrstudio/7236158c6c8d8103b9fb
          window.innerHeight for others for accurate viewport height calculation. */

        // Traffic light height 20px. Need to make a proper solution
        containerHeight = document.documentElement.clientHeight - 20;
        containerWidth = document.documentElement.clientWidth;
      } else {
        containerHeight = gameContainerRef.current.offsetHeight;
        containerWidth = gameContainerRef.current.offsetWidth;
      }

      const viewableArea = containerHeight - bottomOffset;

      let heightUpperBound = (containerWidth * DEFAULT_ASPECT_RATIO) / bottomAllowedCrop;
      const upperBoundCrop = 1 - viewableArea / heightUpperBound;
      if (bottomAllowedCrop < upperBoundCrop || viewableArea + viewableArea * bottomAllowedCrop < heightUpperBound) {
        heightUpperBound = viewableArea / (1 - bottomAllowedCrop);
      } else if (heightUpperBound < containerHeight) {
        heightUpperBound = containerHeight;
      }
      if (heightUpperBound > containerHeight && heightUpperBound / DEFAULT_ASPECT_RATIO > containerWidth) {
        heightUpperBound = Math.max(containerWidth * DEFAULT_ASPECT_RATIO, containerHeight);
      }

      const videoHeight = Math.min(heightUpperBound, containerWidth / minAspectRatio);
      const allowedCropHeight = videoHeight * bottomAllowedCrop;
      const crop = videoHeight - containerHeight > 0 ? videoHeight - containerHeight : 0;
      const overlayBottomOffset = containerHeight - (videoHeight - (overlayHeight || 0));
      const overlayOverlap = overlayBottomOffset > bottomOffset ? overlayBottomOffset - bottomOffset : 0;

      onViewableAreaChange?.(viewableArea);
      const viewableAreaCalculated = viewableArea * 0.55;
      const leaderOffset = classic
        ? Math.min(244, viewableAreaCalculated)
        : Math.min(videoHeight / 2, viewableAreaCalculated);
      setLoaderTopOffset(leaderOffset);
      setVideoHeight(videoHeight);
      setAllowedCropHeight(allowedCropHeight);
      setComputedCrop(crop);
      setOverlayOverlap(overlayOverlap);
    };

    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [classic, bottomAllowedCrop, bottomOffset, gameContainerRef, minAspectRatio, onViewableAreaChange, overlayHeight]);

  useAutomaticallyPosterCreate({ videoRef, setPoster });

  const mods = useModifiers(styles, 'stream', { classic, noTransition });

  if (videoHeight === 0) return null;

  const ambientEnabled = videoRef && posterRef && FeatureConfiguration.isAmbientLightEnabled;

  return (
    <div
      data-testid={classic ? 'classic-stream' : 'stream'}
      className={`${styles.stream} ${mods} ${className}`}
      {...rest}
    >
      <div className={styles.stream__curtainBase}>{curtainBase}</div>
      <div
        className={`${styles.stream__videoContainer}`}
        style={{ '--computedVideoHeight': `${videoHeight}px` } as CSSProperties}
        ref={videoContainerRef}
      >
        {ambientEnabled ? (
          <>
            {isWide ? (
              <AmbientLight videoRef={videoRef} posterRef={posterRef} isVideoPlayingRef={isVideoPlayingRef} />
            ) : null}
            {!isWide && posterBlur ? <PosterBlur poster={poster} /> : null}
          </>
        ) : null}

        <div
          className={`${styles.stream__videoWrapper} ${
            FeatureConfiguration.isAmbientLightEnabled && isWide ? styles.stream__videoWrapper_ambientLight : ''
          }`}
        >
          {FeatureConfiguration.isAmbientLightEnabled && isWide ? null : (
            <>
              <div className={styles.stream__curtainContainer}>
                <div className={styles.stream__curtainOverlay}>{curtainOverlay}</div>
              </div>
              <div className={`${styles.stream__curtainContainer} ${styles.stream__curtainContainer_reverse}`}>
                <div className={styles.stream__curtainOverlay}>{curtainOverlay}</div>
              </div>
            </>
          )}

          <div className={styles.stream__video}>
            <div
              className={styles.stream__videoSpacer}
              style={{ height: videoHeight, width: videoHeight / DEFAULT_ASPECT_RATIO } as CSSProperties}
            ></div>
            <div
              className={styles.stream__videoContent}
              style={{ '--loaderTopOffset': `${loaderTopOffset}px` } as CSSProperties}
            >
              {renderVideo({ loaderTopOffset, setPosterRef, setVideoPlaying, poster })}
            </div>
          </div>
        </div>
      </div>
      <div
        className={styles.stream__overlayContainer}
        style={
          {
            '--allowedCropHeight': `${allowedCropHeight}px`,
            '--computedCrop': `${computedCrop}px`,
            '--overlayOverlap': adaptOverlayHeight ? `${overlayOverlap}px` : undefined,
          } as CSSProperties
        }
      >
        {overlay || <div className={styles.stream__overlay}></div>}
        {overlayHeight !== undefined ? (
          <div
            className={styles.stream__overlayFadeContainer}
            style={
              {
                '--overlayHeightBase': `${overlayHeight}px`,
              } as CSSProperties
            }
          >
            {overlayFade}
          </div>
        ) : null}
      </div>
    </div>
  );
};
